import React, { useState, useEffect, useRef } from 'react';
import { X, Search, FileText, FolderOpen, MessageSquare } from 'lucide-react';
import { dataRoomAPI } from '../../../services/api';

export default function GlobalSearchModal({ open, onClose, dataRoomId, onSelectFile, onSelectFolder }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ files: [], folders: [], questions: [] });
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQ('');
      setResults({ files: [], folders: [], questions: [] });
    }
  }, [open]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults({ files: [], folders: [], questions: [] }); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await dataRoomAPI.search(dataRoomId, q.trim());
        setResults(res.data);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [q, dataRoomId, open]);

  if (!open) return null;

  const highlight = (text, needle) => {
    if (!text || !needle) return text;
    const parts = text.split(new RegExp(`(${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((p, i) =>
      p.toLowerCase() === needle.toLowerCase()
        ? <mark key={i} className="bg-yellow-200 px-0.5">{p}</mark>
        : <span key={i}>{p}</span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-start justify-center p-4 pt-20" data-testid="search-modal">
      <div className="bg-white rounded-lg w-full max-w-2xl shadow-2xl flex flex-col max-h-[80vh]">
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          <Search size={16} className="text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search files, folders, and Q&A... (min 2 chars)"
            className="flex-1 text-sm outline-none"
            data-testid="search-input-modal"
          />
          {loading && <span className="text-xs text-gray-400">Searching...</span>}
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-auto p-3">
          {q.trim().length < 2 ? (
            <div className="py-8 text-center text-xs text-gray-400">Type 2+ characters to search</div>
          ) : (
            <>
              {results.files.length > 0 && (
                <Section title="Files" icon={FileText} count={results.files.length}>
                  {results.files.map(f => (
                    <button
                      key={f.id}
                      onClick={() => { onSelectFile && onSelectFile(f); onClose(); }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded flex gap-2"
                      data-testid={`search-file-${f.id}`}
                    >
                      <FileText size={14} className="text-blue-500 shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">{highlight(f.name, q.trim())}</div>
                        {f.snippet && <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{highlight(f.snippet, q.trim())}</div>}
                        {f.auto_tags?.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {f.auto_tags.slice(0, 3).map(t => (
                              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{t}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </Section>
              )}
              {results.folders.length > 0 && (
                <Section title="Folders" icon={FolderOpen} count={results.folders.length}>
                  {results.folders.map(f => (
                    <button
                      key={f.id}
                      onClick={() => { onSelectFolder && onSelectFolder(f.id); onClose(); }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded flex gap-2"
                      data-testid={`search-folder-${f.id}`}
                    >
                      <FolderOpen size={14} className="text-amber-500 shrink-0" />
                      <div className="text-sm text-gray-900">{highlight(f.name, q.trim())}</div>
                    </button>
                  ))}
                </Section>
              )}
              {results.questions.length > 0 && (
                <Section title="Q&A" icon={MessageSquare} count={results.questions.length}>
                  {results.questions.map(q2 => (
                    <div key={q2.id} className="px-3 py-2 hover:bg-gray-50 rounded" data-testid={`search-q-${q2.id}`}>
                      <div className="text-sm font-medium text-gray-900">{highlight(q2.title, q.trim())}</div>
                      <div className="text-[11px] text-gray-500 line-clamp-1">{highlight(q2.body, q.trim())}</div>
                    </div>
                  ))}
                </Section>
              )}
              {!loading && results.files.length === 0 && results.folders.length === 0 && results.questions.length === 0 && (
                <div className="py-8 text-center text-xs text-gray-400">No results</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, count, children }) {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
        <Icon size={11} /> {title} ({count})
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}
